<template>
    <v-dialog :value="value" :max-width="width" :persistent="blocked" :overlay-opacity="overlayOpacity"
        :fullscreen="fullscreen" :transition="fullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
        @input="$emit('input', $event)" @click:outside="onOutside">
        <v-card class="base-dialog" :class="{ 'base-dialog--fullscreen': fullscreen }">
            <div class="base-dialog__header">
                <span class="base-dialog__title">{{ title }}</span>
                <slot name="header-actions" />
                <button v-if="!hideClose" type="button" class="base-dialog__close" @click="$emit('close')">
                    <v-icon size="16">mdi-close</v-icon>
                </button>
            </div>
            <div v-if="$slots.default" class="base-dialog__body">
                <slot />
            </div>
            <div v-if="$slots.footer" class="base-dialog__footer">
                <slot name="footer" />
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapMutations } from "vuex";

export default {
    name: "BaseDialog",
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        width: {
            type: [String, Number],
            default: 480,
        },
        persistent: {
            type: Boolean,
            default: false,
        },
        hideClose: {
            type: Boolean,
            default: false,
        },
        fullscreen: {
            type: Boolean,
            default: false,
        },
        overlayOpacity: {
            type: [String, Number],
            default: undefined,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState(['dialogStack']),
        blocked(vm = this) {
            return vm.persistent || vm.loading
        },
    },
    methods: {
        ...mapMutations(['pushDialog', 'removeDialog', 'blockDialog', 'unblockDialog']),
        onOutside() {
            if (!this.blocked) this.$emit('close')
        },
    },
    watch: {
        value(val) {
            if (val) {
                this.pushDialog(this._uid)
                if (this.loading) this.blockDialog(this._uid)
            } else this.removeDialog(this._uid)
        },
        dialogStack(stack) {
            if (!this.value || stack.includes(this._uid) || this.loading) return
            this.$emit('close')
        },
        loading(val) {
            if (!this.value) return
            val ? this.blockDialog(this._uid) : this.unblockDialog(this._uid)
        },
    },
    beforeDestroy() {
        this.removeDialog(this._uid)
    },
}
</script>

<style scoped>
.base-dialog {
    background: var(--v-surface-base) !important;
    border: 1px solid var(--v-outline-base);
    border-radius: 14px !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 85vh;
}

.base-dialog--fullscreen {
    border: none;
    border-radius: 0 !important;
    max-height: 100vh;
    height: 100%;
}

.base-dialog__header {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 20px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.base-dialog--fullscreen .base-dialog__header {
    height: calc(56px + var(--safe-area-inset-top, 0px));
    padding-top: var(--safe-area-inset-top, 0px);
}

.base-dialog__title {
    font-size: var(--fs-title);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-right: auto;
    padding-right: 12px;
}

.base-dialog__close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid var(--v-outline2-base);
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: border-color 0.15s, color 0.15s;
}

.base-dialog__close:hover {
    border-color: var(--v-txtSecondary-base);
    color: var(--v-txtPrimary-base);
}

.base-dialog__body {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    padding: 20px;
    overflow-x: hidden;
    overflow-y: auto;
}

.base-dialog__footer {
    --footer-btn-height: 52px;
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.base-dialog--fullscreen .base-dialog__footer ::v-deep .v-btn {
    height: calc(var(--footer-btn-height) + var(--safe-area-inset-bottom, 0px)) !important;
    padding-bottom: var(--safe-area-inset-bottom, 0px);
}
</style>
